import { action, computed, observable, flow, toJS } from 'mobx'; //eslint-disable-line
import _reduce from 'lodash/reduce'; //eslint-disable-line
import _flatten from 'lodash/flatten'; //eslint-disable-line
import _uniqueBy from 'lodash/uniqBy'; //eslint-disable-line
import { createContext } from 'react';
import i18next from 'i18next';
import db from '../utils/localForage';

import goTrackerConfig from 'goTrackerConfig';
import { locationStore } from './locationStore';
import utils from '../utils';
import * as CONSTS from '../utils/constants';

class ViewStore {
  @observable
  language = CONSTS.EN.CODE;

  @observable
  isTwentyFourHour = true;

  @observable
  currentView = this.views.home;

  @observable
  currentSlide = {};

  @observable
  tripRowLimit = goTrackerConfig.tripRowLimit || 4;

  @observable
  serviceDirection = null;

  @observable
  inboundTripIncrement = 1;

  @observable
  outboundTripIncrement = 1;

  @observable
  fav = {
    page: '',
    corridor: '',
    station: '',
  }

  @observable
  favTrips = [];

  @observable
  autoRefreshTime = new Date();

  @observable
  viewPort = {
    width: window.innerWidth,
    height: window.innerHeight,
    isLandscape: false,
  }

  currentTimeLine = null;
  @action
  updateAutoRefreshTime = () => {
    this.autoRefreshTime = new Date();
  }

  @action
  updateViewPort = () => {
    this.viewPort.width = window.innerWidth;
    this.viewPort.height = window.innerHeight;

    if (this.viewPort.width < this.viewPort.height) {
      this.viewPort.isLandscape = !this.viewPort.isLandscape;
    }
  }

  @observable
  isMenuActive = false;

  views = {
    home: CONSTS.VIEW_PATHS.HOME,
    stationDeparture: CONSTS.VIEW_PATHS.STATION_DEPARTURE,
    unionDeparture: CONSTS.VIEW_PATHS.UNION_DEPARTURE,
    tripDetail: CONSTS.VIEW_PATHS.TRIP_DETAIL,
    busDeparture: CONSTS.VIEW_PATHS.BUS_DEPARTURE,
    usbtDeparture: CONSTS.VIEW_PATHS.USBT_DEPARTURE,
    busTripDetail: CONSTS.VIEW_PATHS.BUS_TRIP_DETAIL,
  }

  @action
  setServiceDirection = direction => {
    if (direction !== this.serviceDirection) {
      this.serviceDirection = direction;

      console.log(`Service Direction Selected ${this.serviceDirection}`);
    }
  }

  @action
  incrementTripRowLimit = (direction = '') => {
    switch (direction) {
      case CONSTS.SERVICE_DIRECTION_INBOUND:
        this.inboundTripIncrement++;
        break;
      case CONSTS.SERVICE_DIRECTION_OUTBOUND:
        this.outboundTripIncrement++;
        break;
      case CONSTS.SERVICE_DIRECTION_ALL:
        this.inboundTripIncrement++;
        this.outboundTripIncrement++;
        break;
      default:
        break;
    }
  }

  @computed
  get inboundTripsDisplayed() {
    return this.tripRowLimit * this.inboundTripIncrement;
  }

  @computed
  get outboundTripsDisplayed() {
    return this.tripRowLimit * this.outboundTripIncrement;
  }

  @action
  resetTripRowIncrement = (direction = '') => {
    switch (direction) {
      case CONSTS.SERVICE_DIRECTION_INBOUND:
        this.inboundTripIncrement = 1;
        break;
      case CONSTS.SERVICE_DIRECTION_OUTBOUND:
        this.outboundTripIncrement = 1;
        break;
      case CONSTS.SERVICE_DIRECTION_ALL:
        this.inboundTripIncrement = 1;
        this.outboundTripIncrement = 1;
        break;
      default:
        break;
    }
  }

  @action
  toggleMenu = () => {
    this.isMenuActive = !this.isMenuActive;
  }

  @action
  toggleClock = () => {
    this.isTwentyFourHour = !this.isTwentyFourHour;
    this.saveClockPreference();
  }

  @action
  updateView = viewPath => {
    if (this.currentView !== viewPath) {
      this.currentView = viewPath;
    }
  }

  @action
  toggleLanguage = () => {
    if (this.language === CONSTS.EN.CODE) {
      this.language = CONSTS.FR.CODE;
      i18next.changeLanguage(this.language);
      this.saveLanguage();
    } else {
      this.language = CONSTS.EN.CODE;
      i18next.changeLanguage(this.language);
      this.saveLanguage();
    }
  }

  saveLanguage = () => {
    if (!db.viewSettingStore) return;

    db.viewSettingStore.setItem('language', this.language)
      .then(() => {
        console.log(`${this.language} saved successfully`);
      })
      .catch(err => {
        console.log(err);
      });
  }

  @action
  getClockPreference = () => {
    if (!db.viewSettingStore) return;

    db.viewSettingStore.getItem('clock')
      .then((clockPreference) => {
        this.isTwentyFourHour = clockPreference;
      })
      .catch(err => {
        console.log(err);
      });
  }

  saveClockPreference = () => {
    if (!db.viewSettingStore) return;

    db.viewSettingStore.setItem('clock', this.isTwentyFourHour)
      .then(() => {
        console.log(`24-hour clock: ${this.isTwentyFourHour}, saved successfully`);
      })
      .catch(err => {
        console.log(err);
      });
  }

  startLocalForage = () => {
    db.init();
  }

  @action
  getLanguage = () => {
    if (!db.viewSettingStore) return;

    db.viewSettingStore.getItem('language')
      .then((languagePreference) => {
        if (languagePreference === CONSTS.EN.CODE || languagePreference === CONSTS.FR.CODE) {
          this.language = languagePreference;
          i18next.changeLanguage(this.language);
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  @action
  setFavouritePage = () => {
    if (!db.favStore || !locationStore.corridor || !locationStore.station) return;

    const key = this.getFavKey(this.currentView);
    const data = this.getFavValue(this.currentView);

    //Push favourite page into store
    if (this.favTrips.length < 10) {
      this.favTrips.push({ key: key, value: data });
    }

    //Remove departure info before storage
    data.unionDeparture = null;
    data.stationDeparture = null;

    db.favStore.setItem(key, data)
      .then(() => {
        console.log(`${key} successfully saved as favourite`);
      })
      .catch(err => {
        console.log(err);
      });
  }

  @action
  removeFavPage = key => {
    if (utils.isNullOrEmpty(key)) return;

    db.favStore.removeItem(key)
      .then(() => {
        this.favTrips = this.favTrips.filter(t => t.key !== key);

        console.log(`${key} successfully removed from favourite`);
      })
      .catch(err => {
        console.log(err);
      });
  }

  @action
  getFavPages = async() => {
    if (!db.favStore) return;

    console.log('Retrieving favourite Pages');

    db.favStore.iterate((value, key) => {
      this.favTrips.push({ key: key, value: value });

      console.log(`Getting Favourite Page: ${key}`);
    })
      .then(async() => {
        for (const favTrip of this.favTrips) {
          const data = await this.loadFavouritePageData(favTrip.key, favTrip.value);

          favTrip.value = Object.assign({}, favTrip.value, data);
        }
      })
      .catch(err => {
        console.log(err);
      });
  }

  @action
  loadFavouritePageData = async(key, value) => {
    let newFavTrip = {};
    // const favTrip = this.favTrips.find(t => t.key === key);
    const station = locationStore.stopsDictionary[value.stationCode];

    if (!station) return;

    if (value.trip) {
      const trip = await locationStore.loadTripDetail(value.trip.tripNumber);

      if (trip.data && trip.data.commitmentTrip.length > 0) {
        newFavTrip = Object.assign({}, { trip: trip.data.commitmentTrip[0] });
      }
    } else {
      if (value.isUnionDeparture) {
        const stationName = station ? station.name : '';
        const unionDeparture = await locationStore.loadUnionDepartureMsg(stationName);

        if (unionDeparture.data & unionDeparture.data.trips.length > 0) {
          newFavTrip = Object.assign({}, { unionDeparture: unionDeparture.data.trips });
        }
      }

      const stationId = station ? station.atlsId : '';
      const _stationDeparture = await locationStore.loadStationDepartureMsg(value.corridorCode, stationId);

      if (_stationDeparture.data || _stationDeparture.data.directions.length > 0) {
        newFavTrip = Object.assign(newFavTrip, { stationDeparture: _stationDeparture.data.directions });
      }
    }

    // eslint-disable-next-line consistent-return
    return newFavTrip;
  }

  @action
  getFavKeysFromStorage = async(storageName) => {
    const store = storageName ? storageName : db.favStore;

    if (!store) return;

    let allKeys = [];

    try {
      allKeys = await store.keys();

      console.log(`Favourite keys retrieved: ${allKeys}`);
    } catch(err) {
      console.log(err);
    }

    // eslint-disable-next-line consistent-return
    return allKeys;
  };

  getFavKey = currentView => {
    switch (currentView) {
      case CONSTS.VIEW_PATHS.STATION_DEPARTURE:
        return `${locationStore.corridorCode}-${locationStore.stationCode}-${this.serviceDirection}`;
      case CONSTS.VIEW_PATHS.UNION_DEPARTURE:
        return `${locationStore.corridorCode}-${locationStore.stationCode}-${CONSTS.STATION_CODE_UNION}`;
      case CONSTS.VIEW_PATHS.TRIP_DETAIL && locationStore.trip:
        return `${locationStore.corridorCode}-${locationStore.stationCode}-${locationStore.trip.tripNumber}`;
      default:
        return null;
    }
  }

  getFavValue = currentView => {
    const fav = {
      corridorCode: locationStore.corridorCode,
      stationCode: locationStore.stationCode,
      serviceDirection: this.serviceDirection,
      isUnionDeparture: false,
      stationDeparture: locationStore.messages ? locationStore.messages.directions : null,
      unionDeparture: null,
      trip: null
    };

    if (currentView === CONSTS.VIEW_PATHS.UNION_DEPARTURE) {
      fav.isUnionDeparture = true;
      fav.serviceDirection = CONSTS.SERVICE_DIRECTION_OUTBOUND;
      fav.unionDeparture = locationStore.unionDepartureTrips;
    }

    if (currentView === CONSTS.VIEW_PATHS.TRIP_DETAIL && locationStore.trip) {
      fav.serviceDirection = null;
      fav.trip = window.toJS(locationStore.trip);
    }

    return fav;
  }

  @action
  loadFavCorridorAndStation = () => {
    if (!utils.isNullOrEmpty(this.fav.corridor) && !utils.isNullOrEmpty(this.fav.station)) {
      locationStore.setCorridor(this.fav.corridor);
      locationStore.setStation(this.fav.station);

      console.log('Loading favourite station and corridor');
    }
  }

  @action
  setLastCorridorAndStation = () => {
    if (locationStore.corridor) {
      db.viewSettingStore.setItem('lastCorridorCode', locationStore.corridor.code)
        .then((corridor) => {
          console.log(`${corridor} line successfully saved`);
        })
        .catch(err => {
          console.log(err);
        });
    }

    if (locationStore.station) {
      db.viewSettingStore.setItem('lastStationCode', locationStore.station.code)
        .then((station) => {
          console.log(`${station} station successfully saved`);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }

  @action
  loadLastCorridorAndStation = () => {
    console.log('Loading last corridor and station');

    db.viewSettingStore.getItem('lastCorridorCode')
      .then((corridor) => {
        if (!goTrackerConfig.showBusTerminals && corridor === 'BT') return;

        locationStore.setCorridor(corridor);

        db.viewSettingStore.getItem('lastStationCode')
          .then((station) => {
            locationStore.setStation(station);
          })
          .catch(err => {
            console.log(err);
          });
      })
      .catch(err => {
        console.log(err);
      });
  }

  @action
  getViewSettings = () => {
    // TODO - might need to be async, slow retrieval could flash the wrong UI preferences
    this.getLanguage();
    this.getClockPreference();
    // this.getFavPages();
  }

  @computed
  get languageName() {
    switch (this.language) {
      case CONSTS.FR.CODE:
        return CONSTS.FR.NAME;
      case CONSTS.EN.CODE:
        return CONSTS.EN.NAME;
      default:
        return CONSTS.EN.NAME;
    }
  }

  canShowBusTerminals() {
    return goTrackerConfig.showBusTerminals === true;
  }

  busTerminalsToHide() {
    return goTrackerConfig.busTerminalToHide;
  }
}

const viewStore = (window.viewStore = new ViewStore());
window.toJS = toJS;
export default createContext(viewStore);

export { viewStore };
